/**
 * Customer Trial & Cancelled List Configuration
 * Both tabs share similar structure - customers with services
 */
import type { TFunction } from 'i18next'
import type {
    DataTableColumn,
    FilterItemConfig,
} from '@/components/ui/data-table-customized'
import {
    createChipFormatter,
    createDateFormatter,
    createInternalLinkFormatter,
} from '@/components/ui/data-table-customized'
import type {
    CustomerTrial,
    CustomerTrialService,
} from '@/modules/Customer/types'
import { generatePathDatacentral, paths } from '@/utils/path'
import { Link } from 'react-router-dom'
import {
    renderEmployeeNames,
    renderIndustry,
    SERVICE_STATUS_CHIP_COLORS,
    getServiceListFilters,
    createSourceFilters,
} from './customer-list-shared-config'

// ============================================================================
// Main Customer Columns (parent rows)
// ============================================================================

export const getTrialCustomerColumns = (
    t: TFunction
): DataTableColumn<CustomerTrial>[] => [
    {
        key: 'customer_name',
        title: t('customer_name'),
        width: 350,
        pinned: 'left',
        cellClassName: 'truncate uppercase',
        sortable: true,
        formatter: createInternalLinkFormatter({
            pathKey: 'customerDetail',
            pathParams: (_, row) => ({ id: row?.customer_id?.toString() }),
            label: (_, row) =>
                [row?.customer_id, row?.company_name || row?.customer_name]
                    .filter(Boolean)
                    .join(' - ') || '-',
        }),
    },
    {
        key: 'customer_sale_follower_names',
        title: t('customer_sales_followers'),
        width: 200,
        render: (value) => renderEmployeeNames(value, t('un-sales-assigned')),
    },
    {
        key: 'customer_cs_follower_names',
        title: t('customer_cs_followers'),
        width: 200,
        render: (value) => renderEmployeeNames(value, t('un-cs-assigned')),
    },
    {
        key: 'customer_industry',
        title: t('customer_industry'),
        width: 180,
        sortable: true,
        render: (value) =>
            renderIndustry(value, t('customer_industry.unknown')),
    },
]

/** Cancelled list columns = trial columns + last service off date */
export const getCancelledCustomerColumns = (
    t: TFunction
): DataTableColumn<CustomerTrial>[] => [
    ...getTrialCustomerColumns(t),
    {
        key: 'last_service_off_date',
        title: t('last_service_off_date'),
        width: 150,
        sortable: true,
        cellClassName: 'text-right',
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
]

// ============================================================================
// Expanded Service Columns (child rows)
// ============================================================================

export const getTrialServiceColumns = (
    t: TFunction
): DataTableColumn<CustomerTrialService>[] => [
    {
        key: 'service_name',
        title: t('service'),
        width: 350,
        render: (_, row) => (
            <Link
                to={generatePathDatacentral(paths.serviceDetail, {
                    id: row.service_id,
                })}
                className="text-primary hover:underline uppercase"
            >
                {row.service_id} - {row.product_group_name}
            </Link>
        ),
    },
    {
        key: 'service_status',
        title: t('service_status'),
        width: 150,
        formatter: createChipFormatter<CustomerTrialService>({
            colorMap: SERVICE_STATUS_CHIP_COLORS(t),
            defaultConfig: { color: 'default' },
            size: 'sm',
        }),
    },
    {
        key: 'service_start_date',
        title: t('trial_start_date'),
        width: 150,
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
    {
        key: 'service_stopped_date',
        title: t('trial_end_date'),
        width: 150,
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
    {
        key: 'expectation',
        title: t('expectation'),
        width: 300,
        render: (value) => (
            <span className="truncate block" title={value || ''}>
                {value || '-'}
            </span>
        ),
    },
    {
        key: 'result_poc',
        title: t('result_poc'),
        width: 250,
        render: (value) => (
            <span className="truncate block" title={value || ''}>
                {value || '-'}
            </span>
        ),
    },
]

// ============================================================================
// Filter Configuration
// ============================================================================

/** Trial/Cancelled filters = service list filters + source filters */
export const getTrialFilterItems = (t: TFunction): FilterItemConfig[] => [
    ...getServiceListFilters(t),
    ...createSourceFilters(t),
]

// ============================================================================
// Filter Default Values
// ============================================================================

export const TRIAL_FILTER_DEFAULTS = {
    customer_sale_followers: [],
    customer_cs_followers: [],
    customer_industry: [],
    product_group_name: [],
    customer_source: [],
    customer_source_type: [],
}

// ============================================================================
// Sort Configuration
// ============================================================================

export const TRIAL_SORT_CONFIG = {
    defaultSort: {
        sort_by: 'customer_id',
        sort_order: 'desc' as const,
    },
    sortableFields: ['customer_name', 'customer_industry', 'has_cs'],
}

// ============================================================================
// Helpers
// ============================================================================

/** Get services from customer row */
export const getCustomerServices = (row: CustomerTrial) => row.services || []

/** Check if customer has services */
export const hasServices = (row: CustomerTrial) =>
    row.services && row.services.length > 0
